import React from 'react';

import { useTextOverlay } from '../state/TextOverlayProvider';
import {
  getHtmlString,
  mapDraftEditorDataToTextOverlay,
} from '../style-export-utils';
import { TextOverlayV2 } from '../types';
import useTextOverlayStyle from './useTextOverlayStyle';

interface UseTextOverlaySubmitConfig {
  onHide: () => void;
  onSubmit: (overlay: TextOverlayV2) => void;
}

type UseTextOverlaySubmit = () => void;

const useTextOverlaySubmit = (
  config: UseTextOverlaySubmitConfig,
): UseTextOverlaySubmit => {
  const { onHide, onSubmit } = config;

  const {
    animationsTabExtension,
    canvasSize,
    draftEditorData,
    editorHtmlRef,
    overlayTimingExtension,
    stylePersistorExtension,
    textValue,
  } = useTextOverlay();

  const { textBuilderStyles } = useTextOverlayStyle();

  // animations tab extension removes the animations from the overlay
  // if the extension is not enabled.
  const { onSubmitAnimations } = animationsTabExtension;
  // the timing extension will append the time entry to the output overlay
  // if necessary.
  const { appendTimingToOverlay } = overlayTimingExtension;
  const { onSaveLastEditorState } = stylePersistorExtension;

  return React.useCallback(() => {
    const htmlString = getHtmlString(editorHtmlRef.current);
    onSaveLastEditorState(draftEditorData);

    const customizedTextOverlay = mapDraftEditorDataToTextOverlay(
      draftEditorData,
      htmlString,
      textValue,
      canvasSize,
      textBuilderStyles,
    );

    onSubmit(onSubmitAnimations(appendTimingToOverlay(customizedTextOverlay)));
    onHide();
  }, [
    appendTimingToOverlay,
    canvasSize,
    draftEditorData,
    editorHtmlRef,
    onHide,
    onSaveLastEditorState,
    onSubmit,
    onSubmitAnimations,
    textBuilderStyles,
    textValue,
  ]);
};

export default useTextOverlaySubmit;
